<script>
import SwitchComponent from '/dashboard/components-next/switch/Switch.vue';
import CommentsFilter from './CommentsFilter.vue';
import Comment from './Comment.vue';
import { formatDate } from 'dashboard/helper/dateHelper.js';
import CommentsLayout from '../CommentsLayout.vue';
import { mapGetters } from 'vuex';
import CustomIcon from 'dashboard/components/ui/CustomIcons.vue';
import SuspendMenu from '../../../../components/SuspendMenu.vue';
import AITestChat from './AITestChat.vue';
import ReplyDialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'CommentsCard',
  components: {
    CommentsLayout,
    CommentsFilter,
    SwitchComponent,
    Comment,
    CustomIcon,
    SuspendMenu,
    AITestChat,
    ReplyDialog,
    TextArea,
  },
  props: {
    postInfo: {
      type: Object,
      required: true,
    },
    initialFilterValues: {
      type: Object,
      default: () => ({}),
    },
    postId: {
      type: Number,
      required: true,
    },
    pageId: {
      type: Number,
      required: true,
    },
    selectedComment: {
      type: Object,
      default: () => ({}),
    },
    searchWord: {
      type: String,
      default: '',
    },
  },
  emits: [
    'toggleActive',
    'filter',
    'readMore',
    'deleted',
    'hidden',
    'responded',
    'replyFailed',
  ],
  data() {
    return {
      showReadMore: false,
      showCommentsFilterDisplayMenu: false,
      filterButtonRect: null,
      showAITestChat: false,
      replyCommentId: null,
      replyTargetId: null,
      replyUsername: '',
      replyText: '',
      replyShouldMention: true,
      isSubmittingReply: false,
      dmTargetCommentId: null,
      dmAuthorName: '',
      dmText: '',
      isSubmittingDm: false,
      filter: {},
      pagination: {
        perPage: 10,
        currentPage: 1,
      },
    };
  },
  computed: {
    ...mapGetters({
      commentsList: 'comments/getPostComments',
      uiFlags: 'comments/getUIFlags',
      accountId: 'getCurrentAccountId',
    }),
    filterDropdownStyle() {
      if (!this.filterButtonRect) return { display: 'none' };

      if (
        !this.filterButtonRect.bottom ||
        !this.filterButtonRect.right ||
        !this.filterButtonRect.top
      ) {
        return { display: 'none' };
      }

      const isRTL = document.dir === 'rtl';

      const spaceBelow = Math.max(
        0,
        window.innerHeight - this.filterButtonRect.bottom
      );
      const spaceAbove = Math.max(0, this.filterButtonRect.top);
      const dropdownHeight = 500;

      const openUpwards =
        spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      const rightPosition = Math.max(
        0,
        window.innerWidth - this.filterButtonRect.right
      );

      const styles = {
        [isRTL ? 'left' : 'right']: `${rightPosition}px`,
      };

      if (openUpwards) {
        const bottomPosition = Math.max(
          0,
          window.innerHeight - this.filterButtonRect.top + 8
        );
        styles.bottom = `${bottomPosition}px`;
      } else {
        const topPosition = Math.max(0, this.filterButtonRect.bottom + 8);
        styles.top = `${topPosition}px`;
      }

      return styles;
    },
    filterContentMaxHeight() {
      if (!this.filterButtonRect) return '400px';

      const spaceBelow = window.innerHeight - this.filterButtonRect.bottom;
      const spaceAbove = this.filterButtonRect.top;
      const headerHeight = 80;
      const padding = 32;

      const openUpwards = spaceBelow < 500 && spaceAbove > spaceBelow;
      const availableSpace = openUpwards ? spaceAbove : spaceBelow;

      return `${Math.max(200, Math.min(400, availableSpace - headerHeight - padding))}px`;
    },
    isVideo() {
      return (
        this.postInfo.image_path && this.postInfo.image_path.endsWith('.mp4')
      );
    },
  },
  mounted() {
    this.filter = this.initialFilterValues;
    this.fetchComments();
  },
  methods: {
    formatDate(date) {
      return formatDate(date);
    },
    fetchComments() {
      const filtersToApply = this.formatFilterToRequest(this.filter);

      this.$store.dispatch('comments/getPostComments', {
        pageId: this.pageId,
        postId: this.postId,
        filters: filtersToApply,
        commentText: this.searchWord,
        pagination: this.pagination,
      });
    },
    formatFilterToRequest(filter) {
      return {
        sort: filter.sort?.key ?? 'desc',
        classification: filter.classification?.map(item => item.key) ?? [],
      };
    },
    async submitReply() {
      this.isSubmittingReply = true;
      try {
        const comments = this.commentsList?.comments || [];

        const clickedId = this.replyTargetId || this.replyCommentId;

        const idEquals = (a, b) => String(a) === String(b);

        let top = comments.find(c => c && idEquals(c.id, clickedId));
        let parent = top || null;
        let repliedItem = null;

        if (!top) {
          parent = comments.find(
            c =>
              c &&
              Array.isArray(c.replies) &&
              c.replies.some(r => idEquals(r.id, clickedId))
          );
          if (parent) {
            repliedItem = parent.replies.find(
              r => r && idEquals(r.id, clickedId)
            );
          }
        }

        const topLevelId = parent ? parent.id : clickedId;

        const authorToUse =
          (repliedItem && (repliedItem.from_name || repliedItem.inbox?.name)) ||
          this.replyUsername ||
          (parent && (parent.from_name || parent.inbox?.name)) ||
          '';

        const isFacebook =
          (parent?.post?.source_type || '').toUpperCase() === 'FACEBOOK';

        let mention = '';
        if (authorToUse) {
          if (isFacebook) {
            mention = `${authorToUse} `;
          } else {
            mention = `@${authorToUse} `;
          }
        }

        const message = `${mention}${this.replyText}`.trim();

        await this.$store.dispatch('comments/createReply', {
          pageId: this.pageId,
          postId: this.postId,
          commentId: topLevelId,
          message,
          callback: ({
            success,
            deleted,
            commentId: deletedCommentId,
            error,
          }) => {
            if (success) {
              this.replyText = '';
              this.replyTargetId = null;
              this.replyCommentId = null;
              this.replyUsername = '';
              this.replyShouldMention = true;
              this.$refs.replyDialog?.close();
              this.$emit('responded', topLevelId);
            } else if (deleted) {
              this.replyText = '';
              this.$refs.replyDialog?.close();
              this.$emit('replyFailed', {
                commentId: topLevelId,
                deletedCommentId: deletedCommentId || topLevelId,
                error,
                isMentioned: parent?.metadata?.type === 'MENTION',
              });
            }
          },
        });
      } catch (error) {
        // If error wasn't handled by callback (non-410 errors), show alert
        if (error.response?.status !== 410) {
          // Error handling for non-410 errors
        }
      } finally {
        this.isSubmittingReply = false;
      }
    },
    closeReplyPopup() {
      this.replyText = '';
    },
    closeDmPopup() {
      this.dmText = '';
    },
    openDmPopup(commentId, emittedAuthor = null) {
      const comments = this.commentsList?.comments || [];
      const idEquals = (a, b) => String(a) === String(b);

      let top = comments.find(c => c && idEquals(c.id, commentId));
      let parent = top || null;
      let repliedItem = null;

      if (!top) {
        parent = comments.find(
          c =>
            c &&
            Array.isArray(c.replies) &&
            c.replies.some(r => idEquals(r.id, commentId))
        );
        if (parent) {
          repliedItem = parent.replies.find(
            r => r && idEquals(r.id, commentId)
          );
        }
      }

      const resolvedAuthor =
        (repliedItem && (repliedItem.from_name || repliedItem.inbox?.name)) ||
        (parent && (parent.from_name || parent.inbox?.name)) ||
        '';

      const author = emittedAuthor || resolvedAuthor || '';

      this.dmTargetCommentId = commentId;
      this.dmAuthorName = author;
      this.dmText = '';
      this.$refs.dmDialog?.open();
    },
    async submitDm() {
      if (this.dmTargetCommentId == null) return;

      this.isSubmittingDm = true;
      try {
        const result = await this.$store.dispatch('comments/sendDirectMessage', {
          commentId: this.dmTargetCommentId,
          message: this.dmText,
        });

        this.dmText = '';
        this.$refs.dmDialog?.close();
        this.dmTargetCommentId = null;
        this.dmAuthorName = '';

        useAlert(this.$t('COMMENTS.MODERATION_CARD.DM_SENT_SUCCESS'));

        window.open(
          `/app/accounts/${this.accountId}/conversations/${result.conversation_id}`,
          '_blank'
        );
      } catch (error) {
        useAlert(this.$t('COMMENTS.MODERATION_CARD.DM_SENT_ERROR'));
      } finally {
        this.isSubmittingDm = false;
      }
    },
    deleteComment(commentId) {
      const comments = this.commentsList?.comments || [];
      const idEquals = (a, b) => String(a) === String(b);

      const parent = comments.find(
        c =>
          c &&
          Array.isArray(c.replies) &&
          c.replies.some(r => idEquals(r.id, commentId))
      );

      const parentId = parent ? parent.id : commentId;
      const targetId = commentId;

      return this.$store.dispatch('comments/delete', {
        commentId: targetId,
        pageId: this.pageId,
        postId: this.postId,
        callback: ({
          success,
          deleted,
          commentId: deletedCommentId,
          error,
        }) => {
          if (success) {
            this.$emit('deleted', {
              commentId: parentId,
              replyId: parent ? targetId : null,
              deleted,
              deletedCommentId: deletedCommentId || targetId,
              error,
            });
          }
        },
      });
    },
    hideComment(commentId) {
      const comments = this.commentsList?.comments || [];
      const idEquals = (a, b) => String(a) === String(b);

      const parent = comments.find(
        c =>
          c &&
          Array.isArray(c.replies) &&
          c.replies.some(r => idEquals(r.id, commentId))
      );

      const parentId = parent ? parent.id : commentId;
      const targetId = commentId;

      return this.$store.dispatch('comments/hide', {
        commentId: targetId,
        pageId: this.pageId,
        postId: this.postId,
        callback: ({
          success,
          deleted,
          commentId: deletedCommentId,
          error,
        }) => {
          if (success) {
            this.$emit('hidden', {
              commentId: parentId,
              replyId: parent ? targetId : null,
              deleted,
              deletedCommentId: deletedCommentId || targetId,
              error,
            });
          }
        },
      });
    },
    openReplyPopup(commentId, emittedAuthor = null) {
      const comments = this.commentsList?.comments || [];
      const idEquals = (a, b) => String(a) === String(b);

      let top = comments.find(c => c && idEquals(c.id, commentId));
      let parent = top || null;
      let repliedItem = null;

      if (!top) {
        parent = comments.find(
          c =>
            c &&
            Array.isArray(c.replies) &&
            c.replies.some(r => idEquals(r.id, commentId))
        );
        if (parent) {
          repliedItem = parent.replies.find(
            r => r && idEquals(r.id, commentId)
          );
        }
      }

      const topLevelId = parent ? parent.id : commentId;

      const resolvedAuthor =
        (repliedItem && (repliedItem.from_name || repliedItem.inbox?.name)) ||
        (parent && (parent.from_name || parent.inbox?.name)) ||
        '';

      const author = emittedAuthor || resolvedAuthor || '';

      const isFacebook =
        (parent?.post?.source_type || '').toUpperCase() === 'FACEBOOK';

      this.replyTargetId = commentId;
      this.replyCommentId = topLevelId;
      this.replyUsername = author;
      this.replyShouldMention = !isFacebook;
      this.replyText = '';
      this.$refs.replyDialog?.open();
    },
    handleLoadMore() {
      this.pagination.currentPage += 1;
      this.fetchComments();
    },
    onToggleActive() {
      this.$store.dispatch('posts/toggleActive', {
        pageId: this.pageId,
        postId: this.postId,
      });
      this.$emit('toggleActive');
    },
    openCommentsFilterDisplayMenu() {
      this.showCommentsFilterDisplayMenu = !this.showCommentsFilterDisplayMenu;
      if (this.showCommentsFilterDisplayMenu) {
        this.$nextTick(() => {
          if (this.$refs.filterButton) {
            const rect = this.$refs.filterButton.getBoundingClientRect();
            if (
              rect &&
              rect.top !== undefined &&
              rect.bottom !== undefined &&
              rect.right !== undefined
            ) {
              this.filterButtonRect = rect;
            } else {
              this.showCommentsFilterDisplayMenu = false;
            }
          } else {
            this.showCommentsFilterDisplayMenu = false;
          }
        });
      } else {
        this.filterButtonRect = null;
      }
    },
    onFilterChange(option) {
      this.filter = option;
      this.pagination.currentPage = 1;
      this.showCommentsFilterDisplayMenu = false;
      this.$emit('filter', option);
      this.fetchComments();
    },
    handleReadMore() {
      this.showReadMore = !this.showReadMore;
      this.$emit('readMore', !this.showReadMore);
    },
    handleTestMode() {
      this.showAITestChat = true;
    },
    hideAITestChat() {
      this.showAITestChat = false;
    },
    togglePlay(event) {
      const video = this.$refs.videoElement;
      if (!video) return;
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
      event.stopPropagation();
    },
  },
};
</script>

<template>
  <div class="w-full">
    <div class="bg-white dark:bg-n-solid-2">
      <!-- Post Settings -->
      <div
        class="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-slate-200 dark:border-slate-700"
      >
        <div class="flex items-center gap-3">
          <slot name="card-header-button" />
          <h5
            class="text-sm sm:text-base font-medium text-slate-900 dark:text-white"
          >
            {{ $t('COMMENTS.CARD.ALLOW_REPLIES') }}
          </h5>
        </div>
        <div class="relative flex items-center gap-2">
          <SwitchComponent
            v-model="isSwitchOn"
            :model-value="!!postInfo.active"
            @click="onToggleActive"
          />
          <SuspendMenu>
            <ul class="list-none">
              <li
                class="rounded px-4 py-2 bg-n-solid-2 text-slate-800 dark:text-slate-200 dark:hover:bg-n-alpha-2 hover:bg-n-alpha-1 cursor-pointer"
                @click="handleTestMode"
              >
                {{ $t('COMMENTS.CARD.TEST_MODE') }}
              </li>
            </ul>
          </SuspendMenu>
        </div>
      </div>

      <!-- Post Media -->
      <div class="relative bg-slate-100 dark:bg-slate-800">
        <template v-if="isVideo">
          <video
            ref="videoElement"
            class="w-full aspect-square object-cover cursor-pointer"
            :src="postInfo.image_path"
            :preload="metadata"
            controls
            @click.stop="togglePlay"
          />
        </template>
        <template v-else>
          <img
            class="w-full aspect-square object-cover"
            :class="
              postInfo.comment_count === 0
                ? 'cursor-not-allowed'
                : 'cursor-pointer'
            "
            :src="postInfo.image_path || '/assets/images/placeholder.svg'"
            alt="Post image"
          />
        </template>
      </div>

      <!-- Post Info -->
      <div class="px-4 sm:px-6 py-4 space-y-4">
        <div
          class="flex items-center gap-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="flex-shrink-0"
          >
            <path
              d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12zm0-1A5 5 0 1 1 8 3a5 5 0 0 1 0 10zM7.5 4.5v4h4v-1h-3v-3h-1z"
              fill="currentColor"
            />
          </svg>
          <span>{{ formatDate(postInfo.created_at) }}</span>
        </div>

        <div v-if="postInfo.message" class="space-y-2">
          <p
            class="text-sm sm:text-base text-slate-700 dark:text-slate-300"
            :class="showReadMore ? '' : 'line-clamp-3'"
          >
            {{ postInfo.message }}
          </p>
          <button
            class="text-sm text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-300 font-medium"
            @click="handleReadMore()"
          >
            {{
              showReadMore
                ? $t('COMMENTS.CARD.SHOW_LESS')
                : $t('COMMENTS.CARD.READ_MORE')
            }}
          </button>
        </div>

        <div
          class="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <p
            class="text-sm sm:text-base font-semibold text-slate-900 dark:text-white"
          >
            {{
              $t('COMMENTS.CARD.COMMENT_COUNT', {
                count: commentsList?.pagination?.totalCount || 0,
              })
            }}
          </p>
          <div class="relative">
            <button
              ref="filterButton"
              class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              type="button"
              @click="openCommentsFilterDisplayMenu"
            >
              <CustomIcon group="general" name="FILTER" color="#5A6169" />
            </button>
            <div
              v-if="showCommentsFilterDisplayMenu"
              v-on-clickaway="openCommentsFilterDisplayMenu"
              class="fixed inset-0 z-[100] bg-black/5 dark:bg-black/20 backdrop-blur-[1px]"
              @click="openCommentsFilterDisplayMenu"
            >
              <div
                :style="filterDropdownStyle"
                class="fixed animate-slide-down"
                @click.stop
              >
                <CommentsFilter
                  :initial-state="filter"
                  :max-content-height="filterContentMaxHeight"
                  @filter="onFilterChange"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Comments List -->
      <div class="border-t border-slate-200 dark:border-slate-700">
        <CommentsLayout
          :is-loading="false"
          loading-message="Carregando comentários..."
          :no-records-found="!commentsList.comments.length"
          no-records-message="Nenhum comentário encontrado"
        >
          <template #header />
          <template #body>
            <div class="divide-y divide-slate-200 dark:divide-slate-700">
              <div
                v-for="comment in commentsList.comments"
                :key="comment.id"
                class="transition-colors"
                :class="
                  comment.id === selectedComment.id
                    ? 'bg-slate-50 dark:bg-slate-800/50'
                    : ''
                "
              >
                <Comment
                  :comment="comment"
                  :ui-flags="uiFlags"
                  :hide-comment="hideComment"
                  :delete-comment="deleteComment"
                  :selected="comment.id === selectedComment.id"
                  :open-reply-popup="openReplyPopup"
                  :open-dm-popup="openDmPopup"
                />
              </div>
            </div>

            <!-- Load More Button -->
            <div
              v-if="
                commentsList.pagination.currentPage <
                commentsList.pagination.totalPages
              "
              class="p-4"
            >
              <button
                class="flex w-full p-3 items-center justify-center bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                @click="handleLoadMore()"
              >
                {{ $t('COMMENTS.CARD.LOAD_MORE') }}
              </button>
            </div>

            <!-- Reply Dialog -->
            <ReplyDialog
              ref="replyDialog"
              type="edit"
              :title="$t('COMMENTS.CARD.REPLY_TO', { name: replyUsername })"
              :confirm-button-label="$t('COMMENTS.CARD.SEND')"
              :cancel-button-label="$t('COMMENTS.CARD.CANCEL')"
              :disable-confirm-button="
                !replyText.trim() || replyText.length > 500
              "
              :is-loading="isSubmittingReply"
              width="lg"
              @confirm="submitReply"
              @close="closeReplyPopup"
            >
              <TextArea
                v-model="replyText"
                :placeholder="$t('COMMENTS.CARD.REPLY_PLACEHOLDER')"
                :max-length="500"
                :disabled="isSubmittingReply"
                show-character-count
                auto-height
                autofocus
                min-height="8rem"
                max-height="16rem"
              />
            </ReplyDialog>

            <ReplyDialog
              ref="dmDialog"
              type="edit"
              :title="
                $t('COMMENTS.MODERATION_CARD.SEND_DM_TO', {
                  name: dmAuthorName || '—',
                })
              "
              :confirm-button-label="$t('COMMENTS.MODERATION_CARD.SEND')"
              :cancel-button-label="$t('COMMENTS.MODERATION_CARD.CANCEL')"
              :disable-confirm-button="!dmText.trim() || dmText.length > 1000"
              :is-loading="isSubmittingDm"
              width="lg"
              @confirm="submitDm"
              @close="closeDmPopup"
            >
              <TextArea
                v-model="dmText"
                :placeholder="$t('COMMENTS.MODERATION_CARD.DM_PLACEHOLDER')"
                :max-length="1000"
                :disabled="isSubmittingDm"
                show-character-count
                auto-height
                autofocus
                min-height="8rem"
                max-height="16rem"
              />
            </ReplyDialog>
          </template>
        </CommentsLayout>
      </div>
    </div>
  </div>

  <!-- AI Test Chat Modal -->
  <woot-modal v-model:show="showAITestChat" :on-close="hideAITestChat">
    <AITestChat :key="postInfo?.id" :post="postInfo" />
  </woot-modal>
</template>
